import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Commonly paired with — + toggle, variant options, batch add to cart.
 *
 * @typedef {object} ProductPairedWithRefs
 * @property {HTMLButtonElement} addButton
 * @extends {Component<ProductPairedWithRefs>}
 */
class ProductPairedWithComponent extends Component {
  requiredRefs = ['addButton'];

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'USD';

  connectedCallback() {
    super.connectedCallback();
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || 'USD';
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#syncSubmitVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('.product-paired-with__toggle');
    if (toggle instanceof HTMLButtonElement) {
      this.#onToggleClick(toggle);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target instanceof HTMLInputElement && target.classList.contains('product-paired-with__variant-radio')) {
      const item = target.closest('[data-paired-item]');
      if (item instanceof HTMLElement) {
        this.#updateVariantFromOptions(item);
      }
    }
  };

  /** @param {HTMLButtonElement} toggle */
  #onToggleClick(toggle) {
    if (toggle.disabled) return;

    const item = toggle.closest('[data-paired-item]');
    if (!(item instanceof HTMLElement) || item.dataset.soldOut === 'true') return;

    const selected = item.dataset.selected !== 'true';
    this.#setItemSelected(item, toggle, selected);
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLButtonElement} toggle
   * @param {boolean} selected
   */
  #setItemSelected(item, toggle, selected) {
    item.dataset.selected = selected ? 'true' : 'false';
    item.classList.toggle('is-selected', selected);
    toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');

    if (selected) {
      this.#updateVariantFromOptions(item);
    }

    this.#syncSubmitVisibility();
  }

  addSelectedToCart() {
    const items = this.#getSelectedItems();
    if (items.length === 0) return;

    const { addButton } = this.refs;
    addButton.disabled = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    });

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );
          return;
        }

        let cart;
        try {
          const cartResponse = await fetch('/cart.js');
          cart = await cartResponse.json();
        } catch (error) {
          console.error(error);
        }

        const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-paired-with-component',
            itemCount,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#syncSubmitVisibility();
      });
  }

  /** @param {HTMLElement} item */
  #updateVariantFromOptions(item) {
    const variantId = this.#resolveVariantId(item);
    if (!variantId) return;

    item.dataset.variantId = variantId;

    const variants = this.#getVariants(item);
    const variant = variants?.find((entry) => String(entry.id) === variantId);
    const soldOut = variant ? variant.available === false : false;

    item.dataset.soldOut = soldOut ? 'true' : 'false';
    item.classList.toggle('is-unavailable', soldOut);

    const toggle = item.querySelector('.product-paired-with__toggle');
    if (toggle instanceof HTMLButtonElement) {
      toggle.disabled = soldOut;
      if (soldOut && item.dataset.selected === 'true') {
        this.#setItemSelected(item, toggle, false);
      }
    }

    if (variant) {
      this.#updatePriceDisplay(item, variant);
    }

    this.#syncSubmitVisibility();
  }

  /**
   * @param {HTMLElement} item
   * @param {{ price: number, compare_at_price?: number | null }} variant
   */
  #updatePriceDisplay(item, variant) {
    const priceRoot = item.querySelector('[data-paired-price]');
    if (!priceRoot) return;

    const priceEl = priceRoot.querySelector('.price');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price, this.#moneyFormat, this.#currency);
    }

    const compareEl = priceRoot.querySelector('.compare-at-price');
    if (compareEl && variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareEl.textContent = formatMoney(variant.compare_at_price, this.#moneyFormat, this.#currency);
      compareEl.closest('.price__sale')?.classList.remove('price__hidden');
      priceRoot.querySelector('.price__regular')?.classList.add('price__hidden');
    }
  }

  /** @param {HTMLElement} item */
  #resolveVariantId(item) {
    const variants = this.#getVariants(item);
    if (!variants) {
      return item.dataset.variantId ?? '';
    }

    const optionGroups = item.querySelectorAll('.product-paired-with__option');
    if (optionGroups.length === 0) {
      return item.dataset.variantId ?? '';
    }

    /** @type {string[]} */
    const selectedOptions = [];
    optionGroups.forEach((group) => {
      const checked = group.querySelector('.product-paired-with__variant-radio:checked');
      if (checked instanceof HTMLInputElement) {
        selectedOptions.push(checked.value);
      }
    });

    const match = variants.find((entry) =>
      entry.options.every((option, index) => option === selectedOptions[index])
    );

    return match ? String(match.id) : '';
  }

  /** @param {HTMLElement} item */
  #getVariants(item) {
    const script = item.querySelector('[data-paired-product-variants]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      return /** @type {Array<{id: number, options: string[], available: boolean, price: number, compare_at_price?: number | null}>} */ (
        JSON.parse(script.textContent)
      );
    } catch {
      return null;
    }
  }

  #getSelectedItems() {
    /** @type {Array<{variantId: string, quantity: number}>} */
    const items = [];

    this.querySelectorAll('[data-paired-item][data-selected="true"]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.dataset.soldOut === 'true') return;

      const variantId = element.dataset.variantId;
      if (!variantId) return;

      items.push({ variantId, quantity: 1 });
    });

    return items;
  }

  #syncSubmitVisibility() {
    const { addButton } = this.refs;
    const hasSelection = this.#getSelectedItems().length > 0;
    const wrapper = addButton.closest('[data-paired-submit]');

    if (wrapper instanceof HTMLElement) {
      wrapper.hidden = !hasSelection;
    }

    addButton.disabled = !hasSelection;
  }
}

if (!customElements.get('product-paired-with-component')) {
  customElements.define('product-paired-with-component', ProductPairedWithComponent);
}
